/* ENSSA Reporting — vocabulary tokens.
   Aligned to the locked design brief + actual task battery spec (Mike, Apr 2026).
   Risk language: deficit-free throughout.
   Forbidden: theta, SE, posterior, residual, subscore, subtest, probe,
     "above/below expectation", "as expected".
*/

import type {
  RiskBand,
  RiskMeta,
  RiskCopy,
  CompletionStatus,
  StatusMeta,
  TeachingNote,
  Term,
  TermId,
  TermCode,
  YearLevel,
  YearLevelOption,
  Cohort,
  Task,
} from "@/types";

/* ── Risk bands ──
   Labels intentionally NOT here — they live in src/lib/copy.ts and are
   resolved at usage time via riskLabel(band) / domainRiskLabel(band) so
   the active vocabulary always wins. RiskMeta carries colour tokens
   only. */
const RISK: Record<RiskBand, RiskMeta> = {
  low:       { id: 'low',       color: 'var(--risk-on-track)', bg: 'var(--risk-on-track-bg)', fg: 'var(--risk-on-track-fg)' },
  moderate:  { id: 'moderate',  color: 'var(--risk-emerging)', bg: 'var(--risk-emerging-bg)', fg: 'var(--risk-emerging-fg)' },
  high:      { id: 'high',      color: 'var(--risk-concern)',  bg: 'var(--risk-concern-bg)',  fg: 'var(--risk-concern-fg)' },
  more_info: { id: 'more_info', color: 'var(--ink-400)',       bg: 'var(--ink-100)',          fg: 'var(--fg-2)' },
};
const RISK_ORDER = ['low', 'moderate', 'high', 'more_info'] as const satisfies readonly RiskBand[];

const RISK_COPY: Record<RiskBand, RiskCopy> = {
  low:       { headline: 'Likely on track for this point in the year.',           detail: 'Continue Tier 1 instruction.' },
  moderate:  { headline: 'May benefit from additional support.',                   detail: 'Monitor closely; consider small-group teaching.' },
  high:      { headline: 'May benefit from targeted support.',                     detail: 'Plan structured small-group or individual support.' },
  more_info: { headline: 'No classification — confirm task completion before drawing a conclusion.', detail: 'Confirm task completion or re-administer affected tasks.' },
};

/* ── Screening completion ── */
const STATUS: Record<CompletionStatus, StatusMeta> = {
  completed:  { id: 'completed',  label: 'Completed',   color: 'var(--risk-on-track)' },
  partial:    { id: 'partial',    label: 'Partial',     color: 'var(--risk-emerging)' },
  notstarted: { id: 'notstarted', label: 'Not started', color: 'var(--ink-300)' },
};

/* ── Teaching notes ── */
const TEACHING_NOTE: Record<'priority' | 'needs' | 'monitor' | 'insufficient' | 'none', TeachingNote> = {
  priority:     { id: 'priority',     label: 'Priority focus area',     tone: 'concern' },
  needs:        { id: 'needs',        label: 'May benefit from support', tone: 'emerging' },
  monitor:      { id: 'monitor',      label: 'Worth monitoring',        tone: 'emerging' },
  insufficient: { id: 'insufficient', label: 'No classification',        tone: 'neutral' },
  none:         { id: 'none',         label: '',                        tone: 'neutral' },
};

/* ── 2026 single-administration reporting window.
   Internally this reuses the T1 battery slot while JSON should prefer
   assessments["2026-T2"]. Legacy T1 assessment keys remain accepted in
   the adapter during transition. */
const TERMS: readonly Term[] = [
  { id: '2026-T2', label: '2026 Term 2 screening', t: 'T1' },
];
const TERM_DEFAULT: TermId = '2026-T2';

const YEAR_LEVELS: readonly YearLevelOption[] = [
  { id: 'F',  label: 'Foundation' },
  { id: 'Y1', label: 'Year 1' },
];

/* ─────────────────────────────────────────────────────────────
   TASK BATTERY SPEC
   Source: Mike's notes, Apr 2026.
   • STPM and STDD excluded (non-numeracy).
   • Decomposition V1 (T1) and V2 (T3/T4) shown with same label —
     different item IDs, same construct/scale, never co-occur.
   • AADD/ASDD (drag-drop) and AAMC/ASMC (multi-choice) are distinct.
   • 5–7 tasks per battery; layout must handle that range.
   ───────────────────────────────────────────────────────────── */

/* All possible task definitions (superset across all batteries) */
/* Timed families show "Correct per minute"; untimed families (Number
   Line, Decomposition) show a dash for that metric. */
const TASK_DEFS: Record<string, Task> = {
  /* Foundation shared */
  mc20:    { id: 'mc20',    label: 'Magnitude Comparison',            detail: '0–20',  timed: true,  itemsTotal: 60 },
  bnl20:   { id: 'bnl20',   label: 'Number Line',                    detail: '0–20',  timed: false, itemsTotal: 10 },
  /* Foundation cohort-A specific */
  mnc20:   { id: 'mnc20',   label: 'Missing Number',                  detail: '0–20',  timed: true,  itemsTotal: 30 },
  mq10:    { id: 'mq10',    label: 'Match Quantity',                  detail: '1–10',  timed: true,  itemsTotal: 30 },
  dmt5:    { id: 'dmt5',    label: 'Decomposition',                   detail: '5',     timed: false, itemsTotal: 6 },
  /* Foundation cohort-B specific */
  mna20:   { id: 'mna20',   label: 'Missing Number',                  detail: '0–20',  timed: true,  itemsTotal: 30 },
  mq20:    { id: 'mq20',    label: 'Match Quantity',                  detail: '1–20',  timed: true,  itemsTotal: 30 },
  dmt10:   { id: 'dmt10',   label: 'Decomposition',                  detail: '10',    timed: false, itemsTotal: 6 },
  /* Year 1 shared */
  mc100:   { id: 'mc100',   label: 'Magnitude Comparison',           detail: '0–100', timed: true,  itemsTotal: 60 },
  bnl100:  { id: 'bnl100',  label: 'Number Line',                    detail: '0–100', timed: false, itemsTotal: 13 },
  /* Year 1 cohort-A specific */
  mna100:  { id: 'mna100',  label: 'Missing Number',                  detail: '0–100', timed: true,  itemsTotal: 29 },
  /* Year 1 cohort-B specific */
  mnc100:  { id: 'mnc100',  label: 'Missing Number',                  detail: '0–100', timed: true,  itemsTotal: 29 },
  /* Year 1 arithmetic (format varies by term) */
  aadd:    { id: 'aadd',    label: 'Addition',                       detail: 'drag-drop',    timed: true, itemsTotal: 17 },
  asdd:    { id: 'asdd',    label: 'Subtraction',                    detail: 'drag-drop',    timed: true, itemsTotal: 19 },
  aamc:    { id: 'aamc',    label: 'Addition',                       detail: 'multi-choice', timed: true, itemsTotal: 40 },
  asmc:    { id: 'asmc',    label: 'Subtraction',                    detail: 'multi-choice', timed: true, itemsTotal: 30 },
};

/*
  Battery lookup: TASK_BATTERIES[year][cohort][termCode] → array of task IDs.
  termCode: 'T1' | 'T3' | 'T4'
  Cohorts: 'A' (Foundation-A / Year1-A) or 'B' (Foundation-B / Year1-B)
*/
const TASK_BATTERIES: Record<YearLevel, Record<Cohort, Partial<Record<TermCode, readonly string[]>>>> = {
  F: {
    A: {
      T1: ['mq20','mc20','mnc20','dmt10','bnl20'],
      T3: ['mq20','mc20','mnc20','dmt10','bnl20'],
      T4: ['mq20','mc20','mnc20','dmt10','bnl20'],
    },
    B: {
      T1: ['mq20','mc20','mnc20','dmt10','bnl20'],
      T3: ['mq20','mc20','mnc20','dmt10','bnl20'],
      T4: ['mq20','mc20','mnc20','dmt10','bnl20'],
    },
  },
  Y1: {
    A: {
      T1: ['mc100','mnc100','aamc','asmc','bnl100'],
      T3: ['mc100','mnc100','aamc','asmc','bnl100'],
      T4: ['mc100','mnc100','aamc','asmc','bnl100'],
    },
    B: {
      T1: ['mc100','mnc100','aamc','asmc','bnl100'],
      T3: ['mc100','mnc100','aamc','asmc','bnl100'],
      T4: ['mc100','mnc100','aamc','asmc','bnl100'],
    },
  },
};

/* Helper: get the battery for a given (year, cohort, termId) */
function getBattery(year: YearLevel, cohort: Cohort, termId: TermId | string): Task[] {
  const termObj = TERMS.find(t => t.id === termId) ?? TERMS[0];
  if (!termObj) return [];
  const t = termObj.t;
  const yearMap = TASK_BATTERIES[year];
  const cohortMap = yearMap?.[cohort] ?? {};
  const ids = cohortMap[t] ?? cohortMap['T1'] ?? [];
  return ids
    .map(id => TASK_DEFS[id])
    .filter((task): task is Task => task != null);
}

/* Current 2026 Year 1 battery, used as fallback for aggregate charts. */
const TASKS: Task[] = getBattery('Y1', 'A', TERM_DEFAULT);

export {
  RISK, RISK_ORDER, RISK_COPY, STATUS, TASKS, TASK_DEFS, TASK_BATTERIES,
  TEACHING_NOTE, TERMS, TERM_DEFAULT, YEAR_LEVELS, getBattery,
};
